import { isEqual } from 'lodash-es';
import { type AnyAction } from 'redux';
import { type ThunkDispatch } from 'redux-thunk';

export type SimpleStore<S> = {
  getState(): S;
  dispatch: ThunkDispatch<S, void, AnyAction>;
};

export type ActionWithDependencies<S extends SimpleStore<any>, R> = (
  helpers: ActionDependencyHelpers<S>
) => R;

export interface ActionDependencyHelpers<S extends SimpleStore<any>> {
  dispatch: (...args: Parameters<S['dispatch']>) => void;
  selector: <R>(selector: (state: ReturnType<S['getState']>) => R) => R;
}

export async function runActionWithDependencies<S extends SimpleStore<any>, R>(
  action: ActionWithDependencies<S, R>,
  store: S
): Promise<R> {
  const lastSelectorResults: any[][] = [];
  for (let i = 0; i < 100; i++) {
    const dispatches: Array<Promise<any>> = [];
    const selectorResults: any[] = [];
    const res = action({
      dispatch: (...args: any[]) => {
        const result = (store.dispatch as (...a: any[]) => any)(...args);
        dispatches.push(result as Promise<any>);
      },
      selector: (selector) => {
        const result = selector(store.getState());
        selectorResults.push(result);
        return result;
      },
    });

    await Promise.all(dispatches);
    if (
      isEqual(lastSelectorResults[lastSelectorResults.length - 1] ?? [], selectorResults)
    ) {
      return res;
    } else {
      lastSelectorResults.push(selectorResults);
      if (lastSelectorResults.length > 4) {
        lastSelectorResults.shift();
      }
    }
  }

  throw new Error(
    `Infinite loop in action with dependencies. Last selector results were: ${JSON.stringify(
      lastSelectorResults
    )}`
  );
}
